/**
 * Theme Context — light/dark mode and brand accent (Default blue vs Healthesus).
 * Context value is memoized per React guidance to limit consumer re-renders.
 */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useLayoutEffect,
  useMemo,
} from 'react';
import { PORTAL_APPEARANCE_FROM_OPENER_EVENT } from '../constants/appearanceQueryConstants';
import { DEFAULT_BRAND_ACCENT, type BrandAccentId } from '../constants/brandAccentConstants';
import { DEFAULT_THEME_MODE, ThemeMode, type ThemeModeId } from '../constants/themeConstants';
import {
  BRAND_ACCENT_STORAGE_KEY,
  readBrandAccentFromStorage,
  readThemeFromStorage,
  THEME_STORAGE_KEY,
} from '../constants/storageKeyConstants';

export type { BrandAccentId as BrandAccent } from '../constants/brandAccentConstants';
export type { ThemeModeId as Theme } from '../constants/themeConstants';

export { BRAND_ACCENT_STORAGE_KEY, THEME_STORAGE_KEY } from '../constants/storageKeyConstants';

/** Updates `<meta name="theme-color">` from computed `--meta-theme-color` (theme/accent CSS). */
function syncMetaThemeColor(): void {
  const root = document.documentElement;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) return;
  const raw = getComputedStyle(root).getPropertyValue('--meta-theme-color').trim();
  if (raw) meta.setAttribute('content', raw);
}

interface ThemeContextType {
  theme: ThemeModeId;
  toggleTheme: () => void;
  setTheme: (theme: ThemeModeId) => void;
  isTransitioning: boolean;
  brandAccent: BrandAccentId;
  setBrandAccent: (accent: BrandAccentId) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

function readStoredBrandAccent(): BrandAccentId {
  return readBrandAccentFromStorage() ?? DEFAULT_BRAND_ACCENT;
}

const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isInitialMount = useRef(true);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const [theme, setThemeState] = useState<ThemeModeId>(() => readThemeFromStorage() ?? DEFAULT_THEME_MODE);

  const [brandAccent, setBrandAccentState] = useState<BrandAccentId>(() => readStoredBrandAccent());

  useLayoutEffect(() => {
    const root = document.documentElement;
    root.dataset.brand = brandAccent;
    syncMetaThemeColor();
  }, [brandAccent]);

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = theme;

    if (isInitialMount.current) {
      if (theme === ThemeMode.DARK) {
        root.classList.add('dark-mode', 'dark');
        root.classList.remove('light-mode');
      } else {
        root.classList.add('light-mode');
        root.classList.remove('dark-mode', 'dark');
      }
      isInitialMount.current = false;
      try {
        localStorage.setItem(THEME_STORAGE_KEY, theme);
      } catch (error) {
        console.error('Failed to save theme to localStorage:', error);
      }
      syncMetaThemeColor();
      return;
    }

    setIsTransitioning(true);
    root.classList.add('theme-transitioning');

    requestAnimationFrame(() => {
      if (theme === ThemeMode.DARK) {
        root.classList.add('dark-mode', 'dark');
        root.classList.remove('light-mode');
      } else {
        root.classList.add('light-mode');
        root.classList.remove('dark-mode', 'dark');
      }
      try {
        localStorage.setItem(THEME_STORAGE_KEY, theme);
      } catch (error) {
        console.error('Failed to save theme to localStorage:', error);
      }

      syncMetaThemeColor();

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            root.classList.remove('theme-transitioning');
            setIsTransitioning(false);
          }, 16);
        });
      });
    });
  }, [theme]);

  const setBrandAccent = useCallback((accent: BrandAccentId) => {
    setBrandAccentState(accent);
    try {
      localStorage.setItem(BRAND_ACCENT_STORAGE_KEY, accent);
    } catch (error) {
      console.error('Failed to save brand accent to localStorage:', error);
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => (prev === ThemeMode.LIGHT ? ThemeMode.DARK : ThemeMode.LIGHT));
  }, []);

  const setTheme = useCallback((newTheme: ThemeModeId) => {
    setThemeState(newTheme);
  }, []);

  useEffect(() => {
    const syncFromOpenerStorage = (): void => {
      try {
        setBrandAccentState(readStoredBrandAccent());
        const nextTheme = readThemeFromStorage();
        if (nextTheme) setThemeState(nextTheme);
      } catch {
        /* ignore */
      }
    };
    window.addEventListener(PORTAL_APPEARANCE_FROM_OPENER_EVENT, syncFromOpenerStorage);
    return () => window.removeEventListener(PORTAL_APPEARANCE_FROM_OPENER_EVENT, syncFromOpenerStorage);
  }, []);

  const contextValue = useMemo(
    () => ({
      theme,
      toggleTheme,
      setTheme,
      isTransitioning,
      brandAccent,
      setBrandAccent,
    }),
    [theme, toggleTheme, setTheme, isTransitioning, brandAccent, setBrandAccent]
  );

  return <ThemeContext.Provider value={contextValue}>{children}</ThemeContext.Provider>;
};

function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}

export { ThemeProvider, useTheme };